import { useEffect, useState } from 'react'
import { FilterActions } from '@/screens/history/components/filter-sheet-parts'
import { BottomSheet } from '@/ui/bottom-sheet'
import {
  EMPTY_STOCKTAKE_FILTER,
  parseThreshold,
  type StocktakeFilter,
  thresholdToInput,
} from '../filters'

/**
 * The Progress-tab filter sheet: two numeric "greater than" thresholds.
 *
 * Draft state lives here and is re-seeded from `filter` whenever the sheet
 * opens, so dismissing without applying discards the edit — the same contract
 * as `my-orders-filter-sheet`. `FilterActions` is reused for the stacked
 * Apply/Clear buttons; `FilterSheetShell` is NOT, because it hardcodes a date
 * section this sheet has no use for.
 */
function StocktakeFilterSheet({
  open,
  filter,
  onClose,
  onApply,
}: Readonly<{
  open: boolean
  filter: StocktakeFilter
  onClose: () => void
  onApply: (next: StocktakeFilter) => void
}>) {
  const [valueDraft, setValueDraft] = useState('')
  const [qtyDraft, setQtyDraft] = useState('')

  useEffect(() => {
    if (open) {
      setValueDraft(thresholdToInput(filter.valueGreaterThan))
      setQtyDraft(thresholdToInput(filter.qtyGreaterThan))
    }
  }, [open, filter])

  return (
    <BottomSheet
      dataTour="stocktake-filter-sheet"
      open={open}
      onClose={onClose}
      title="Filter"
      scrollable={false}
    >
      <div
        data-tour="stocktake-filter-sheet-inputs"
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-4"
      >
        <ThresholdField
          id="stocktake-filter-value"
          label="Value greater than"
          placeholder="Enter amount"
          value={valueDraft}
          onChange={setValueDraft}
        />
        <ThresholdField
          id="stocktake-filter-qty"
          label="Quantity greater than"
          placeholder="Enter quantity"
          value={qtyDraft}
          onChange={setQtyDraft}
        />
      </div>
      <div className="shrink-0 bg-background pt-4">
        <FilterActions
          applyDataTour="stocktake-filter-apply-btn"
          onApply={() =>
            onApply({
              valueGreaterThan: parseThreshold(valueDraft),
              qtyGreaterThan: parseThreshold(qtyDraft),
            })
          }
          onClear={() => {
            setValueDraft(thresholdToInput(EMPTY_STOCKTAKE_FILTER.valueGreaterThan))
            setQtyDraft(thresholdToInput(EMPTY_STOCKTAKE_FILTER.qtyGreaterThan))
          }}
          clearDisabled={!valueDraft && !qtyDraft}
        />
      </div>
    </BottomSheet>
  )
}

function ThresholdField({
  id,
  label,
  placeholder,
  value,
  onChange,
}: Readonly<{
  id: string
  label: string
  placeholder: string
  value: string
  onChange: (next: string) => void
}>) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-bold text-foreground">
        {label}
      </label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-3xl border border-border bg-card px-6 text-sm font-medium text-foreground outline-none placeholder:text-muted-foreground"
      />
    </div>
  )
}

export { StocktakeFilterSheet }
